import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, Sparkles } from "lucide-react";
import DOMPurify from "dompurify";
import { API } from "../lib/api";

const SESSION_KEY = "mobicars_ai_session";

function renderMarkdown(text) {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  let html = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const lines = html.split("\n").map((l) => {
    const trimmed = l.trim();
    if (/^[-*]\s+/.test(trimmed)) {
      return `• ${trimmed.replace(/^[-*]\s+/, "")}`;
    }
    return l;
  });
  return DOMPurify.sanitize(lines.join("<br/>"), { ALLOWED_TAGS: ["strong", "br"], ALLOWED_ATTR: [] });
}

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${API}/assistant/history/${sessionId.current}`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data) && data.length) setMessages(data);
        })
        .catch((e) => console.error("Failed to load AI history:", e));
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const raw = part.replace(/^data: ?/, "");
          if (raw === "[DONE]") continue;
          let content;
          try {
            content = JSON.parse(raw);
          } catch {
            content = raw;
          }
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + content };
            return c;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Erreur de connexion à l'assistant." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [
    "Quels véhicules ont un contrôle urgent ?",
    "Quelles vidanges sont bientôt à faire ?",
    "Résume l'état de ma flotte",
  ];

  return (
    <>
      {!open && (
        <button
          data-testid="ai-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#FBBF24] font-bold px-5 py-3.5 rounded-full nb-btn"
        >
          <Bot strokeWidth={2.5} size={22} /> Assistant Mobi
        </button>
      )}

      {open && (
        <div
          data-testid="ai-panel"
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-sm h-[560px] max-h-[80vh] flex flex-col nb-card overflow-hidden mc-slide-up"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#FBBF24] border-b-2 border-slate-900">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#4F46E5] border-2 border-slate-900 flex items-center justify-center">
                <Bot className="text-white" strokeWidth={2.5} size={20} />
              </div>
              <div>
                <p className="font-heading font-black leading-none">Mobi</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Assistant flotte</p>
              </div>
            </div>
            <button data-testid="ai-close-button" onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg bg-white nb-border flex items-center justify-center">
              <X strokeWidth={2.5} size={16} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto mc-scroll p-4 space-y-3 bg-[#FDFBFE]">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="inline-flex items-center gap-2 text-slate-500 font-medium text-sm mb-4">
                  <Sparkles size={16} strokeWidth={2.5} className="text-[#4F46E5]" />
                  Posez-moi une question sur votre flotte
                </div>
                <div className="space-y-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`ai-suggestion-${i}`}
                      onClick={() => setInput(s)}
                      className="block w-full text-left text-sm font-semibold px-3 py-2 rounded-xl bg-white nb-border hover:bg-slate-50 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`ai-msg-${m.role}-${i}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl border-2 border-slate-900 text-sm font-medium ${
                    m.role === "user" ? "bg-[#4F46E5] text-white" : "bg-white"
                  }`}
                >
                  {m.role === "assistant" ? (
                    m.content ? (
                      <span dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                    ) : streaming && i === messages.length - 1 ? (
                      "…"
                    ) : (
                      ""
                    )
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t-2 border-slate-900 bg-white flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Votre message..."
              className="nb-input flex-1 px-3 py-2.5 text-sm font-medium"
            />
            <button
              data-testid="ai-send-button"
              onClick={send}
              disabled={streaming}
              className="w-11 bg-[#4F46E5] text-white rounded-xl nb-btn flex items-center justify-center disabled:opacity-50"
            >
              <Send strokeWidth={2.5} size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
